import type { ReactNode } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { useLocale } from '../i18n'
import { Disclosure } from './Disclosure'

/**
 * C5: what an operator needs and the counter does not (raw errors, versions,
 * fingerprints), closed until asked for. A native disclosure: it opens by
 * keyboard and on iPadOS 16.4, with no script of its own. A pane of its own on
 * a screen; inside a section's pane, a well, "sunk into the pane, not a box
 * drawn on it" (DESIGN.md, as `SubPanel`); inside a well, on the well itself
 * (`plain`, UI-2g). The look is `Disclosure`'s.
 */
export function TechnicalDetails({
  children,
  surface = 'pane',
}: {
  children: ReactNode
  surface?: 'pane' | 'well' | 'plain'
}) {
  const { messages: m } = useLocale()
  return (
    <Disclosure summary={m.app.technicalDetails} surface={surface} className="technical">
      <div className="flex flex-col gap-3 text-sm">{children}</div>
    </Disclosure>
  )
}

/**
 * C5: an outside system's own codes and texts (CaRGOS's "record_invalid", the
 * worker's "token: HTTP 503", Stripe's "insufficient_funds") are for whoever
 * looks after the system, in a well under "Dettagli tecnici"; nobody else sees
 * them. The sentence beside them says what happened in words, for everyone.
 */
export function TechnicalLines({
  lines,
  surface = 'well',
}: {
  lines: readonly string[]
  /** `plain` inside a well already there (UI-2g). */
  surface?: 'well' | 'plain'
}) {
  const { access } = useAccess()
  if (lines.length === 0 || !can(access, 'system.view')) return null
  return (
    <TechnicalDetails surface={surface}>
      {lines.map((line) => (
        <p key={line} className="break-words">
          {line}
        </p>
      ))}
    </TechnicalDetails>
  )
}
